import { Link } from "react-router-dom";
import { Phone, Camera } from "lucide-react";
import { BUSINESS } from "@/lib/constants";
import { track, EVENTS } from "@/lib/analytics";

/**
 * Original-A1-Screening-style hero — text-driven, dark navy bg, big uppercase
 * headline with a cyan-accented city/key phrase. Yellow primary CTA, cyan
 * outlined secondary CTA.
 *
 * Props:
 *  - eyebrow: small cyan label above headline
 *  - title: string OR JSX (use <span className="a1-accent">…</span> to highlight)
 *  - subtitle
 *  - primaryCta: { to, label }
 *  - secondaryCta: { href, label, icon? }
 *  - tertiary: { to, label }
 *  - media: optional { photo, badge } — when present, renders the right-side
 *      photo card on desktop. `photo` must be a CompletedWorkPhoto-shaped
 *      record from /data/galleryPhotos.js. `badge` is a short, neutral
 *      string like "Since 1999" displayed on the card.
 */
export default function Hero({
    eyebrow,
    title,
    subtitle,
    primaryCta = { to: "/free-estimate", label: "Call Now for Free Estimate" },
    secondaryCta,
    tertiary,
    media = null,
}) {
    const computedSecondary = secondaryCta || {
        to: "/upload-photos",
        label: "Upload Photos for Estimate Review",
        icon: Camera,
    };

    const hasMedia = !!media?.photo;
    const textColClass = hasMedia
        ? "lg:col-span-7"
        : "lg:col-span-12 max-w-4xl";

    return (
        <section
            className="a1-hero-bg pt-12 pb-12 md:pt-20 md:pb-16 lg:pt-24 lg:pb-20"
            data-testid="hero-section"
        >
            <div className="a1-container">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
                    <div className={`a1-fade-up ${textColClass}`}>
                        {eyebrow ? (
                            <p className="a1-eyebrow mb-5">{eyebrow}</p>
                        ) : (
                            <p className="a1-eyebrow mb-5">Family-Owned · Serving Southwest Florida Since 1999</p>
                        )}
                        <h1 className="mb-6 leading-[1.05]" data-testid="hero-h1">
                            {title}
                        </h1>
                        {subtitle ? (
                            <p
                                className="text-lg md:text-xl max-w-2xl mb-9 leading-relaxed"
                                style={{ color: "var(--color-text-muted)" }}
                                data-testid="hero-subtitle"
                            >
                                {subtitle}
                            </p>
                        ) : null}
                        <div className="flex flex-col sm:flex-row gap-3 a1-fade-up-delay-1">
                            {primaryCta.href ? (
                                <a
                                    href={primaryCta.href}
                                    onClick={() => track(EVENTS.PHONE_CLICK, { placement: "hero" })}
                                    className="a1-btn-primary"
                                    data-testid="hero-primary-cta"
                                >
                                    <Phone size={18} strokeWidth={2.5} /> {primaryCta.label}
                                </a>
                            ) : (
                                <Link
                                    to={primaryCta.to}
                                    className="a1-btn-primary"
                                    data-testid="hero-primary-cta"
                                >
                                    <Phone size={18} strokeWidth={2.5} /> {primaryCta.label}
                                </Link>
                            )}
                            {computedSecondary.href ? (
                                <a
                                    href={computedSecondary.href}
                                    onClick={() => track(EVENTS.PHONE_CLICK, { placement: "hero-secondary" })}
                                    className="a1-btn-secondary"
                                    data-testid="hero-secondary-cta"
                                >
                                    {computedSecondary.icon ? <computedSecondary.icon size={18} /> : null}
                                    {computedSecondary.label}
                                </a>
                            ) : (
                                <Link
                                    to={computedSecondary.to}
                                    className="a1-btn-secondary"
                                    data-testid="hero-secondary-cta"
                                >
                                    {computedSecondary.icon ? <computedSecondary.icon size={18} /> : null}
                                    {computedSecondary.label}
                                </Link>
                            )}
                        </div>
                        {tertiary ? (
                            <Link
                                to={tertiary.to}
                                className="a1-btn-ghost mt-5 inline-flex"
                                data-testid="hero-tertiary-link"
                            >
                                {tertiary.label} →
                            </Link>
                        ) : null}
                        {/* Trust pill row directly under CTAs */}
                        <div className="mt-8 pt-7 border-t border-[color:var(--color-line)] flex flex-wrap gap-x-8 gap-y-3 a1-fade-up-delay-2">
                            {HERO_PILLS.map((label) => (
                                <span key={label} className="a1-check-pill">
                                    <CheckIcon /> {label}
                                </span>
                            ))}
                        </div>
                    </div>

                    {hasMedia ? (
                        <div
                            className="lg:col-span-5 hidden lg:block a1-fade-up-delay-1"
                            data-testid="hero-media"
                        >
                            <HeroMediaCard photo={media.photo} badge={media.badge} caption={media.caption} />
                        </div>
                    ) : null}
                </div>
            </div>
        </section>
    );
}

function HeroMediaCard({ photo, badge = "Since 1999", caption = "A1 Screening Project Photo · Naples & SW Florida" }) {
    const base = photo.base_path;
    return (
        <figure className="a1-hero-media-card" data-testid="hero-media-card">
            <div className="a1-hero-media-img-wrap">
                <picture>
                    <source
                        type="image/webp"
                        srcSet={`${base}-md.webp 800w, ${base}-lg.webp 1600w`}
                        sizes="(min-width: 1024px) 40vw, 100vw"
                    />
                    <source
                        type="image/jpeg"
                        srcSet={`${base}-md.jpg 800w, ${base}-lg.jpg 1600w`}
                        sizes="(min-width: 1024px) 40vw, 100vw"
                    />
                    <img
                        src={`${base}-md.jpg`}
                        alt={photo.alt}
                        width={photo.intrinsic?.w}
                        height={photo.intrinsic?.h}
                        loading="eager"
                        decoding="async"
                        className="a1-hero-media-img"
                    />
                </picture>
                <span className="a1-hero-media-badge">
                    <span className="a1-hero-media-badge-num">{badge}</span>
                    <span className="a1-hero-media-badge-lbl">Family-Owned · SWFL</span>
                </span>
            </div>
            <figcaption className="a1-hero-media-cap">
                <span className="a1-hero-media-cap-dot" aria-hidden="true" />
                {caption}
            </figcaption>
        </figure>
    );
}

const HERO_PILLS = [
    "Free Estimates",
    "Family-Owned Since 1999",
    "Naples-Based",
    "Clean Workmanship",
];

function CheckIcon() {
    return (
        <span
            aria-hidden="true"
            className="inline-flex items-center justify-center"
            style={{
                width: 18,
                height: 18,
                borderRadius: 999,
                border: "1.5px solid var(--color-accent)",
                color: "var(--color-accent)",
            }}
        >
            <svg viewBox="0 0 12 12" width="10" height="10" fill="none">
                <path d="M2.5 6.5l2.5 2.5L9.5 4" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
        </span>
    );
}
